import type { FinanceReconciliation, ReconciliationFinding } from '@gatecheckpro/contracts';
import { Card, LoadingRegion, Notice, Status } from '@gatecheckpro/design-system';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Whether the finance data still believes itself.
 *
 * The distinction this page exists to preserve is between two kinds of non-zero
 * number that look identical in a list and mean opposite things.
 *
 * A `MUST_BE_ZERO` check restates an invariant the database already enforces.
 * A non-zero answer there does **not** mean somebody has work to do — it means
 * the enforcement is not working, which is an incident. A constraint can prove
 * what it refuses to write; it can say nothing about what is already written,
 * and nothing at all if it is dropped or bypassed.
 *
 * A `WORK_QUEUE` check counts things legitimately waiting for a person. Non-zero
 * is ordinary and often correct.
 *
 * Rendering both as "findings" with a count beside them would train an operator
 * to treat the first like the second, which is precisely the habit that lets a
 * broken invariant sit in a queue for a week.
 *
 * There is no control on this page. Every remedy it might tempt somebody into —
 * editing a posting, forcing a refund's state, correcting a balance — either
 * does not exist or belongs to an authority nobody has decided on, and a button
 * shipped ahead of that decision would be making it.
 */
export function FinanceReconciliationPage(): React.JSX.Element {
  const { resource } = useResource<FinanceReconciliation>('/api/v1/ops/finance/reconciliation');

  return (
    <main className="gc-main">
      <div className="gc-container gc-stack gc-stack--loose">
        <div className="gc-page-header">
          <span className="gc-eyebrow">Finance</span>
          <h1>Reconciliation</h1>
          <p className="gc-muted">
            What the finance data says about itself. Read-only: corrections are compensating entries
            made by server authority, never edits from this console.
          </p>
        </div>

        {resource.status === 'loading' && <LoadingRegion label="Running the checks" />}
        {resource.status === 'failed' && <FailureNotice error={resource.error} />}

        {resource.status === 'loaded' && (
          <>
            {/*
              The headline is about invariants only, and says which kind of
              trouble it is. "Everything is zero" would be wrong on a healthy
              platform with a queue of refunds awaiting a decision.
            */}
            <Notice
              tone={resource.data.invariantsHold ? 'positive' : 'critical'}
              title={
                resource.data.invariantsHold
                  ? 'Every invariant holds'
                  : 'An invariant the database enforces has been broken'
              }
            >
              {resource.data.invariantsHold
                ? 'Nothing here contradicts what the schema refuses to write. Work queues below may still have entries, which is ordinary.'
                : 'A check that must be zero is not. This is not a queue to work through — it means enforcement is not doing what it is supposed to, and it needs somebody now.'}
            </Notice>

            <Section
              title="Invariants"
              blurb="Each restates something the database refuses to write. Any non-zero answer is an incident, not a task."
              findings={resource.data.findings.filter(
                (finding) => finding.expectation === 'MUST_BE_ZERO',
              )}
            />

            <Section
              title="Waiting for a person"
              blurb="Ordinary work. A number here is not a fault; a number that keeps climbing is worth asking about."
              findings={resource.data.findings.filter(
                (finding) => finding.expectation === 'WORK_QUEUE',
              )}
            />

            <p className="gc-muted">
              Generated {new Date(resource.data.generatedAt).toLocaleString()}
            </p>
          </>
        )}
      </div>
    </main>
  );
}

function Section({
  title,
  blurb,
  findings,
}: {
  title: string;
  blurb: string;
  findings: ReconciliationFinding[];
}): React.JSX.Element {
  return (
    <Card title={title} eyebrow={blurb}>
      <div className="gc-stack gc-stack--tight">
        {findings.map((finding) => {
          const broken = finding.expectation === 'MUST_BE_ZERO' && finding.count > 0;
          return (
            <div
              key={finding.check}
              className="gc-summary__row"
              data-testid={`finding-${finding.check}`}
            >
              <span>{finding.check.replaceAll('_', ' ').toLowerCase()}</span>
              <span className="gc-row">
                <span className="gc-mono">{finding.count}</span>
                <Status
                  tone={broken ? 'critical' : finding.count > 0 ? 'info' : 'positive'}
                  label={broken ? 'Must be zero' : finding.count > 0 ? 'Waiting' : 'Clear'}
                />
              </span>
              {/*
                Ids only, and only when there is something to look at. A
                reconciliation report is read by operators across every tenant,
                and there is no reason for it to carry an organizer's
                commercial detail onto this screen.
              */}
              {finding.sample.length > 0 && (
                <span className="gc-muted gc-mono" data-testid={`sample-${finding.check}`}>
                  {finding.sample.join(' ')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
